/**
 * DiameterEditTool — passive tool for editing diameter measurements by dragging
 * the p1/p2 endpoints on the oblique (centerline slice) viewport.
 *
 * Registered as a passive tool on 2D viewports. It detects hover/drag interactions
 * on the DiameterOverlay managed by SizingRenderer2DSlice.
 */

import { DiameterOverlay } from '../../display/overlay-actors/diameter-overlay';
import type { DragHandle } from '../../display/types';
import { add, distance, projectPointOntoPlane, subtract } from '../../domain';
import type { Vec3 } from '../../domain';
import type { InteractionEvent } from '../../interaction/types';
import type { SizingStateService } from '../../services/sizing/sizing-state-service';
import { ObliqueSliceViewport2D } from '../../viewports/oblique-slice-viewport-2d';
import type { IViewport } from '../../viewports/types';
import type { ITool } from '../types';

interface DragState {
    handle: 'p1' | 'p2';
    originalP1: Vec3;
    originalP2: Vec3;
    /** World-space offset from cursor to handle position at grab time */
    grabOffset: Vec3;
    overlay: DiameterOverlay;
}

export class DiameterEditTool implements ITool {
    static readonly toolName = 'diameter-edit';

    readonly id = DiameterEditTool.toolName;
    readonly name = 'Diameter Edit';
    readonly cursor = 'default';
    completed = false;

    private dragState: DragState | null = null;

    constructor(private readonly sizingStateService: SizingStateService) {}

    // ═══════════════════════════════════════════
    //  ITool lifecycle
    // ═══════════════════════════════════════════

    activate(_viewport: IViewport): void {
        // Passive tool bypass the activate calls in interaction service
        // Use event's viewport instead
    }

    deactivate(): void {
        this.dragState = null;
    }

    cancel(): void {
        if (this.dragState) {
            this.restoreOriginal();
            this.dragState.overlay.draggingHandle = null;
            this.dragState = null;
        }
    }

    // ═══════════════════════════════════════════
    //  Event handling
    // ═══════════════════════════════════════════

    handleEvent(event: InteractionEvent): boolean {
        switch (event.type) {
            case 'pointer_move':
                return this.handlePointerMove(event);
            case 'pointer_down':
                return this.handlePointerDown(event);
            case 'mouse_drag':
                return this.handleMouseDrag(event);
            case 'mouse_drag_end':
                return this.handleMouseDragEnd(event);
            case 'key_down':
                return this.handleKeyDown(event);
            default:
                return false;
        }
    }

    // ═══════════════════════════════════════════
    //  Event handlers
    // ═══════════════════════════════════════════

    private handlePointerMove(event: InteractionEvent): boolean {
        if (this.dragState) return false;

        const overlay = this.findDiameterOverlay(event.viewport);
        if (!overlay) return false;

        const canvasX = event.currentPoints.canvas[0];
        const canvasY = event.currentPoints.canvas[1];

        const handle = this.findHandleAtPosition(overlay, canvasX, canvasY);

        const prevHovered = overlay.hoveredHandle;
        overlay.hoveredHandle = handle ? (handle.id as 'p1' | 'p2') : null;

        const prevResetHover = overlay.hoveredResetButton;
        overlay.hoveredResetButton = !handle && overlay.hitTestResetButton(canvasX, canvasY);

        if (overlay.hoveredHandle !== prevHovered || overlay.hoveredResetButton !== prevResetHover) {
            event.viewport.getOverlayLayer().render();
        }

        return false;
    }

    private handlePointerDown(event: InteractionEvent): boolean {
        if (event.button !== 0) return false;

        const overlay = this.findDiameterOverlay(event.viewport);
        if (!overlay) return false;

        const canvasX = event.currentPoints.canvas[0];
        const canvasY = event.currentPoints.canvas[1];

        // Reset button takes priority over drag handles
        if (overlay.hitTestResetButton(canvasX, canvasY)) {
            const nodeId = this.getNodeIdFromViewport(event.viewport);
            if (nodeId !== null) {
                this.sizingStateService.restoreDiameter(nodeId);
                overlay.hoveredResetButton = false;
                event.viewport.getOverlayLayer().render();
            }
            return true;
        }

        const handle = this.findHandleAtPosition(overlay, canvasX, canvasY);
        if (!handle) return false;

        const handleId = handle.id as 'p1' | 'p2';
        overlay.draggingHandle = handleId;

        const handleWorld = handleId === 'p1' ? overlay.getP1World() : overlay.getP2World();
        const cursorWorld = this.projectToSlicePlane(event.currentPoints.world, event.viewport);
        const grabOffset = subtract(handleWorld, cursorWorld);

        this.dragState = {
            handle: handleId,
            originalP1: overlay.getP1World(),
            originalP2: overlay.getP2World(),
            grabOffset,
            overlay,
        };
        return true;
    }

    private handleMouseDrag(event: InteractionEvent): boolean {
        if (!this.dragState) return false;

        const worldPos = event.currentPoints.world;
        if (!worldPos) return false;

        const projected = add(this.projectToSlicePlane(worldPos, event.viewport), this.dragState.grabOffset);

        if (this.dragState.handle === 'p1') {
            this.dragState.overlay.setP1World(projected);
        } else {
            this.dragState.overlay.setP2World(projected);
        }

        event.viewport.getOverlayLayer().render();
        return true;
    }

    private handleMouseDragEnd(event: InteractionEvent): boolean {
        if (!this.dragState) return false;

        const worldPos = event.currentPoints.world;
        if (worldPos) {
            const projected = add(
                this.projectToSlicePlane(worldPos, event.viewport),
                this.dragState.grabOffset
            );

            if (this.dragState.handle === 'p1') {
                this.dragState.overlay.setP1World(projected);
            } else {
                this.dragState.overlay.setP2World(projected);
            }
        }

        // Commit the edit
        const p1 = this.dragState.overlay.getP1World();
        const p2 = this.dragState.overlay.getP2World();
        const newDiameter = distance(p1, p2);
        const nodeId = this.getNodeIdFromViewport(event.viewport);

        if (nodeId !== null) {
            this.sizingStateService.editDiameter(nodeId, newDiameter, p1, p2);
        }

        this.dragState.overlay.draggingHandle = null;
        this.dragState = null;
        event.viewport.getOverlayLayer().render();
        return true;
    }

    private handleKeyDown(event: InteractionEvent): boolean {
        if (event.key !== 'Escape' || !this.dragState) return false;

        this.restoreOriginal();
        this.dragState.overlay.draggingHandle = null;
        this.dragState = null;
        event.viewport.getOverlayLayer().render();
        return true;
    }

    // ═══════════════════════════════════════════
    //  Private helpers
    // ═══════════════════════════════════════════

    private findDiameterOverlay(viewport: IViewport): DiameterOverlay | null {
        const overlayLayer = viewport.getOverlayLayer();
        const obj = overlayLayer.findOverlayByPrefix('sizing-2d-diameter-');
        if (obj instanceof DiameterOverlay) return obj;

        return null;
    }

    private findHandleAtPosition(
        overlay: DiameterOverlay,
        canvasX: number,
        canvasY: number
    ): DragHandle | null {
        const handles: DragHandle[] = [
            {
                id: 'p1',
                position: overlay.getP1Canvas(),
                cursor: 'grab',
                hitRadius: overlay.getHandleHitRadius(),
            },
            {
                id: 'p2',
                position: overlay.getP2Canvas(),
                cursor: 'grab',
                hitRadius: overlay.getHandleHitRadius(),
            },
        ];

        for (const handle of handles) {
            const dx = canvasX - handle.position.x;
            const dy = canvasY - handle.position.y;
            if (dx * dx + dy * dy <= handle.hitRadius * handle.hitRadius) {
                return handle;
            }
        }
        return null;
    }

    private getNodeIdFromViewport(viewport: IViewport): number | null {
        if (!(viewport instanceof ObliqueSliceViewport2D)) return null;
        const centerline = viewport.getActiveCenterline();
        if (!centerline) return null;
        return centerline.getNodeAt(viewport.sliceIndex).id;
    }

    private projectToSlicePlane(worldPos: Vec3, viewport: IViewport): Vec3 {
        if (!viewport || !(viewport instanceof ObliqueSliceViewport2D)) return worldPos;

        return projectPointOntoPlane(worldPos, viewport.slicePlaneOrigin, viewport.slicePlaneNormal);
    }

    private restoreOriginal(): void {
        if (!this.dragState) return;
        this.dragState.overlay.setP1World(this.dragState.originalP1);
        this.dragState.overlay.setP2World(this.dragState.originalP2);
    }
}
